import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearch, useNavigate } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import {
  Activity,
  Calendar,
  CheckCircle2,
  Clock,
  Download,
  FileWarning,
  Globe,
  RotateCw,
  XCircle,
} from 'lucide-react';
import { healthTier, type Audit, type AuditIssue, type AuditPageSummary, type Severity } from '@seo/types';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/spinner';
import { ErrorBanner } from '@/components/error-banner';
import { DetailError } from '@/components/detail-page';
import { StatusPill, humanStatus } from '@/components/status-pill';
import { PageHeader } from '@/components/page-header';
import { GlowCard, GlassSurface } from '@/components/glow-card';
import { ScoreRing } from '@/components/score-ring';
import { useToast, toastError } from '@/components/ui/toast';
import { isActionableCheck } from '@/features/audits/actionable-checks';
import { buildOverviewInsights } from '@/features/audits/build-overview-insights';
import { useAudit } from '@/features/audits/use-audit';
import {
  useAuditIssue,
  useAuditIssues,
  useAuditProgress,
} from '@/features/audits/use-audit-issues';
import { useAuditPages, useAuditPageDetail } from '@/features/audits/use-audit-pages';
import { useCancelAudit } from '@/features/audits/use-cancel-audit';
import { useResumeNarration } from '@/features/audits/use-resume-narration';
import { useQueryClient } from '@tanstack/react-query';
import { useProject } from '@/features/projects/use-project';
import { useAuthStore } from '@/lib/auth';
import { downloadAuthed } from '@/lib/download';
import { fadeUp, stagger } from '@/components/motion';
import { cn } from '@/lib/cn';

const NON_TERMINAL = new Set([
  'queued',
  'discovering',
  'crawling',
  'checking',
  'analyzing',
  'enriching',
  'scoring',
  'narrating',
  'rendering',
]);

type Tab = 'overview' | 'issues' | 'pages';

export function AuditDetailRoute(): JSX.Element {
  const { auditId } = useParams({ strict: false }) as { auditId: string };
  const search = useSearch({ strict: false }) as {
    tab?: Tab;
    issue?: string;
    page?: string;
    severity?: Severity;
  };
  const navigate = useNavigate();
  const audit = useAudit(auditId);
  const progress = useAuditProgress(auditId, audit.data?.status);
  const issues = useAuditIssues(auditId, { limit: 200 });
  const pages = useAuditPages(auditId, { limit: 200 });
  useProject(audit.data?.project_id);
  const cancel = useCancelAudit(auditId);
  const accessToken = useAuthStore((s) => s.accessToken);
  const tenant = useAuthStore((s) => s.tenant);
  const { toast } = useToast();

  if (audit.error) {
    return (
      <div className="space-y-4">
        <DetailError
          error={audit.error}
          entityKind="audit"
          backTo="/audits"
          backLabel="Audits"
        />
      </div>
    );
  }
  if (audit.isLoading || !audit.data) {
    return (
      <div className="flex justify-center p-10">
        <Spinner />
      </div>
    );
  }

  const a = audit.data;
  const nonTerminal = NON_TERMINAL.has(a.status);
  // `tab` is normalized by the route's validateSearch — unknown values are
  // stripped, so this can only be a real tab (default: overview).
  const tab: Tab = (search.tab ?? 'overview') as Tab;

  // Tab changes and drawer OPENS push history entries so the Back button
  // undoes them; drawer CLOSE replaces so closing doesn't grow the stack.
  const setTab = (t: Tab): void => {
    void navigate({ search: (prev) => ({ ...(prev as object), tab: t }) as never });
  };
  const openIssue = (id: string | undefined): void => {
    void navigate({
      search: (prev) => ({ ...(prev as object), issue: id }) as never,
      ...(id === undefined ? { replace: true } : {}),
    });
  };
  const openPage = (id: string | undefined): void => {
    void navigate({
      search: (prev) => ({ ...(prev as object), page: id }) as never,
      ...(id === undefined ? { replace: true } : {}),
    });
  };

  const handleDownloadPdf = async (): Promise<void> => {
    try {
      await downloadAuthed({
        url: `/v1/audits/${a.id}/report.pdf`,
        filename: `audit-${a.id.slice(0, 8)}.pdf`,
        accessToken,
        tenantId: tenant?.id,
      });
    } catch (e) {
      toastError(toast, e);
    }
  };

  const handleDownloadCsv = async (): Promise<void> => {
    try {
      await downloadAuthed({
        url: `/v1/audits/${a.id}/export`,
        filename: `audit-issues-${a.id.slice(0, 8)}.csv`,
        accessToken,
        tenantId: tenant?.id,
      });
    } catch (e) {
      toastError(toast, e);
    }
  };

  const failedButScored = a.status === 'failed' && a.health_score != null;
  const hasAiPartial =
    failedButScored ||
    (a.status === 'completed' &&
      a.warnings.some(
        (w) =>
          (w.severity ?? 'warning') === 'info' &&
          (w.code === 'llm.rate_limited' || w.code === 'llm.summary_failed'),
      ));

  const headerActions = (
    <>
      <StatusPill status={a.status} />
      {hasAiPartial ? (
        <span
          className="inline-flex items-center gap-1 rounded-full border border-sky-500/40 bg-sky-500/10 px-2 py-0.5 text-xs font-medium text-sky-200"
          title="Score and findings are ready. AI explanations are missing — click Retry AI explanations (no re-crawl needed)."
        >
          AI explanations pending
        </span>
      ) : null}
      {nonTerminal ? (
        <Button
          variant="outline"
          disabled={cancel.isPending}
          onClick={async () => {
            try {
              await cancel.mutateAsync();
              toast({ title: 'Audit stopped' });
            } catch (e) {
              toastError(toast, e);
            }
          }}
        >
          <XCircle className="h-4 w-4" />
          Cancel
        </Button>
      ) : (
        <Button variant="outline" onClick={() => audit.refetch()} disabled={audit.isFetching}>
          <RotateCw className={cn('h-4 w-4', audit.isFetching && 'animate-spin')} />
          Refresh
        </Button>
      )}
      <Button
        variant="outline"
        onClick={handleDownloadCsv}
        disabled={a.status !== 'completed' && !failedButScored}
      >
        <Download className="h-4 w-4" />
        Download CSV
      </Button>
      <Button onClick={handleDownloadPdf} disabled={a.status !== 'completed'}>
        <Download className="h-4 w-4" />
        Download PDF
      </Button>
    </>
  );

  return (
    <motion.div variants={stagger} initial="hidden" animate="show" className="space-y-6">
      <PageHeader
        title={`Audit — ${new Date(a.created_at).toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })}`}
        eyebrow="Site audit"
        description={
          nonTerminal
            ? `Checking up to ${a.scope.max_pages} pages of your site — this usually takes a few minutes.`
            : `Checked up to ${a.scope.max_pages} pages · ${
                a.scope.render_js === 'always'
                  ? 'ran JavaScript for every page'
                  : a.scope.render_js === 'auto'
                    ? 'ran JavaScript when needed'
                    : 'HTML only'
              }`
        }
        actions={headerActions}
      />

      {nonTerminal ? <LiveProgressBanner audit={a} progress={progress.data} /> : null}

      {a.warnings.length > 0 ||
      failedButScored ||
      (a.progress as { resume_narration?: { status?: string } } | undefined)?.resume_narration ? (
        <AuditNotices
          auditId={a.id}
          audit={a}
          notices={a.warnings}
          failedButScored={failedButScored}
        />
      ) : null}

      <TabSwitcher tab={tab} setTab={setTab} counts={{ issues: issues.data?.items.length ?? 0, pages: pages.data?.items.length ?? 0 }} />

      {tab === 'overview' ? (
        <OverviewTab
          audit={a}
          totalIssues={issues.data?.items.length ?? 0}
          issues={issues.data?.items ?? []}
          pages={pages.data?.items ?? []}
        />
      ) : tab === 'issues' ? (
        <IssuesTab
          items={issues.data?.items ?? []}
          isLoading={issues.isLoading}
          error={issues.error}
          onOpen={openIssue}
          initialSeverity={search.severity}
        />
      ) : (
        <PagesTab
          items={pages.data?.items ?? []}
          isLoading={pages.isLoading}
          error={pages.error}
          onOpen={openPage}
        />
      )}

      {search.issue ? (
        <IssueDrawer auditId={auditId} issueId={search.issue} onClose={() => openIssue(undefined)} />
      ) : null}
      {search.page ? (
        <PageDrawer auditId={auditId} pageId={search.page} onClose={() => openPage(undefined)} />
      ) : null}
    </motion.div>
  );
}

/**
 * Health score tier labels — aligned to Lighthouse SEO scoring cutoffs
 * (90/50) via `healthTier()` from @seo/types. Users cross-referencing
 * against Google PageSpeed Insights see the same tier for the same score.
 */
function tierLabel(score: number): string {
  return healthTier(score).label;
}

function TabSwitcher({
  tab,
  setTab,
  counts,
}: {
  tab: Tab;
  setTab: (t: Tab) => void;
  counts: { issues: number; pages: number };
}): JSX.Element {
  const items: Array<{ key: Tab; label: string; badge?: number }> = [
    { key: 'overview', label: 'Overview' },
    { key: 'issues', label: 'Issues', badge: counts.issues },
    { key: 'pages', label: 'Pages', badge: counts.pages },
  ];
  return (
    <div className="border-b border-border/60">
      <div className="flex gap-1">
        {items.map((it) => {
          const active = tab === it.key;
          return (
            <button
              key={it.key}
              onClick={() => setTab(it.key)}
              className={cn(
                'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
                active
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )}
            >
              {it.label}
              {it.badge !== undefined && it.badge > 0 ? (
                <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs">{it.badge}</span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function LiveProgressBanner({
  audit: _a,
  progress,
}: {
  audit: Audit;
  progress?: {
    pages_discovered: number | null;
    pages_crawled: number | null;
    pages_failed: number | null;
    issues_count: number | null;
    progress: Record<string, unknown>;
  };
}): JSX.Element {
  const phase = (progress?.progress as { phase?: string; note?: string } | undefined) ?? {};
  return (
    <GlassSurface className="p-4">
      <div className="mb-1 flex items-center justify-between text-xs uppercase tracking-wider text-muted-foreground">
        <span>Live progress</span>
        <span>{phase.phase ?? 'in progress'}</span>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div>
          <div className="text-xs text-muted-foreground">Pages found</div>
          <div className="text-lg font-semibold">{progress?.pages_discovered ?? '—'}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Pages checked</div>
          <div className="text-lg font-semibold">{progress?.pages_crawled ?? '—'}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Skipped</div>
          <div className="text-lg font-semibold">{progress?.pages_failed ?? '—'}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Issues found</div>
          <div className="text-lg font-semibold">{progress?.issues_count ?? '—'}</div>
        </div>
      </div>
      {phase.note ? <div className="mt-2 text-xs text-muted-foreground">{phase.note}</div> : null}
    </GlassSurface>
  );
}

function OverviewTab({
  audit,
  totalIssues,
  issues,
  pages,
}: {
  audit: Audit;
  totalIssues: number;
  issues: AuditIssue[];
  pages: AuditPageSummary[];
}): JSX.Element {
  const a = audit;
  const insights = useMemo(
    () => buildOverviewInsights(a, issues, pages),
    [a, issues, pages],
  );

  return (
    <motion.section variants={fadeUp} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-[240px_minmax(0,1fr)]">
          <GlowCard glow className="flex">
            <div className="flex w-full flex-col items-center justify-center gap-2 p-6">
              {a.health_score !== null ? (
                <ScoreRing score={a.health_score} size={156} label={tierLabel(a.health_score)} />
              ) : (
                <div className="flex h-[156px] w-[156px] flex-col items-center justify-center rounded-full border border-dashed border-border/60">
                  <span className="font-mono text-3xl text-muted-foreground">—</span>
                  <span className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                    {humanStatus(a.status)}
                  </span>
                </div>
              )}
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Globe className="h-3 w-3" />
                health · {a.id.slice(0, 6)}
              </div>
            </div>
          </GlowCard>
          <GlassSurface className="space-y-3 p-4">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              AI summary
            </div>
            <p className="text-sm leading-relaxed text-foreground/90">{insights.aiSummary}</p>
            <div className="text-xs text-muted-foreground">{totalIssues} issues found</div>
          </GlassSurface>
        </div>
        <MetaSidebar audit={a} issueCount={totalIssues} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <GlassSurface className="p-4">
          <div className="mb-3 text-sm font-semibold">SEO score by category</div>
          <div className="space-y-2">
            {insights.categoryScores.map((row) => (
              <div key={row.label} className="grid grid-cols-[110px_1fr_36px] items-center gap-2 text-sm">
                <span className="text-muted-foreground">{row.label}</span>
                <div className="h-2 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-primary"
                    style={{ width: `${row.score}%` }}
                  />
                </div>
                <span className="tabular-nums text-right text-xs">{row.score}</span>
              </div>
            ))}
          </div>
        </GlassSurface>

        <GlassSurface className="p-4">
          <div className="mb-2 text-sm font-semibold">Traffic impact</div>
          <p className="mb-2 text-xs text-muted-foreground">Fixing these issues could improve:</p>
          <div className="mb-3 flex flex-wrap gap-1.5">
            {insights.trafficImpact.levers.map((l) => (
              <span
                key={l}
                className="rounded-full border border-border/60 bg-muted/40 px-2.5 py-0.5 text-xs"
              >
                {l}
              </span>
            ))}
          </div>
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Potential organic gain
            </div>
            <div className="mt-1 text-2xl font-bold text-emerald-700 dark:text-emerald-400">
              {insights.trafficImpact.potentialGain}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {insights.trafficImpact.gainNote}
            </div>
          </div>
        </GlassSurface>
      </div>

      <GlassSurface className="p-4">
        <div className="mb-3 text-sm font-semibold">Top 10 priority fixes</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="py-2 pr-3">Priority</th>
                <th className="py-2 pr-3">Issue</th>
                <th className="py-2 pr-3">Est. SEO impact</th>
                <th className="py-2 pr-3">Effort</th>
                <th className="py-2">Pages</th>
              </tr>
            </thead>
            <tbody>
              {insights.priorityFixes.map((row) => (
                <tr key={row.priority} className="border-t border-border/50">
                  <td className="py-2 pr-3 tabular-nums">{row.priority}</td>
                  <td className="py-2 pr-3 font-medium">{row.issue}</td>
                  <td className="py-2 pr-3">{row.impact}</td>
                  <td className="py-2 pr-3">{row.effort}</td>
                  <td className="py-2 tabular-nums">{row.affectedPages}</td>
                </tr>
              ))}
              {insights.priorityFixes.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-6 text-center text-muted-foreground">
                    No issues to prioritize yet.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </GlassSurface>

      <div className="grid gap-4 lg:grid-cols-2">
        <GlassSurface className="p-4">
          <div className="mb-3 text-sm font-semibold">Before vs after</div>
          <div className="space-y-3">
            {insights.beforeAfter.map((row) => (
              <div key={row.label}>
                <div className="mb-1 text-xs font-medium">{row.label}</div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-md border border-border/60 p-3">
                    <div className="text-[10px] uppercase text-muted-foreground">Current</div>
                    <div className="mt-0.5 text-sm font-semibold">{row.current}</div>
                  </div>
                  <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3">
                    <div className="text-[10px] uppercase text-muted-foreground">Target</div>
                    <div className="mt-0.5 text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                      {row.target}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {insights.beforeAfter.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nothing to compare yet.</p>
            ) : null}
          </div>
        </GlassSurface>

        <GlassSurface className="p-4">
          <div className="mb-3 text-sm font-semibold">Page priority</div>
          <ul className="space-y-3">
            {insights.pagePriority.map((row) => (
              <li key={row.label} className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-medium">{row.label}</div>
                  <div className="text-xs text-muted-foreground">{row.hint}</div>
                </div>
                <div className="shrink-0 text-amber-500">{row.starDisplay}</div>
              </li>
            ))}
          </ul>
        </GlassSurface>
      </div>
    </motion.section>
  );
}

function IssuesTab({
  items,
  isLoading,
  error,
  onOpen,
  initialSeverity,
}: {
  items: AuditIssue[];
  isLoading: boolean;
  error: unknown;
  onOpen: (id: string) => void;
  /** Seed from `?severity=` so shared filter URLs actually apply. */
  initialSeverity?: string;
}): JSX.Element {
  const [severity, setSeverity] = useState<Severity | 'all'>(() =>
    initialSeverity === 'critical' ||
    initialSeverity === 'high' ||
    initialSeverity === 'medium' ||
    initialSeverity === 'low'
      ? initialSeverity
      : 'all',
  );
  const [showAllChecks, setShowAllChecks] = useState(false);
  const rows = useMemo(() => {
    let out = items;
    if (!showAllChecks) {
      out = out.filter((i) => isActionableCheck(i.check_id));
    }
    if (severity !== 'all') {
      out = out.filter((i) => i.severity === severity);
    }
    return out;
  }, [items, severity, showAllChecks]);
  const totalActionable = useMemo(
    () => items.filter((i) => isActionableCheck(i.check_id)).length,
    [items],
  );
  if (error) return <ErrorBanner error={error} />;
  if (isLoading)
    return (
      <div className="flex justify-center p-10">
        <Spinner />
      </div>
    );
  return (
    <GlassSurface className="p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {(['all', 'critical', 'high', 'medium', 'low'] as const).map((s) => (
          <button
            key={s}
            onClick={() => setSeverity(s)}
            className={cn(
              'rounded-full border px-3 py-1 text-xs',
              severity === s
                ? 'border-primary text-primary'
                : 'border-border/60 text-muted-foreground',
            )}
          >
            {s}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
          <span>
            {showAllChecks
              ? `Showing all ${items.length} issues`
              : `Showing ${totalActionable} of ${items.length} — 8 actionable checks`}
          </span>
          <button
            onClick={() => setShowAllChecks((v) => !v)}
            className="rounded-md border border-border/60 px-2 py-1 text-xs hover:border-primary/60 hover:text-primary"
          >
            {showAllChecks ? 'Show only 8 key checks' : 'Show all 23 checks'}
          </button>
        </div>
      </div>
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase text-muted-foreground">
          <tr>
            <th className="py-2">Title</th>
            <th>Severity</th>
            <th>Bucket</th>
            <th>URLs</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((i) => (
            <tr
              key={i.id}
              className="cursor-pointer border-t border-border/40 hover:bg-muted/30"
              onClick={() => onOpen(i.id)}
            >
              <td className="py-2 pr-2">
                <div className="font-medium">{i.title}</div>
                <div className="text-xs text-muted-foreground">{i.summary}</div>
              </td>
              <td>
                <SeverityBadge severity={i.severity} />
              </td>
              <td className="text-xs text-muted-foreground">{i.bucket}</td>
              <td className="text-xs">{i.affected_url_count}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 ? (
        <div className="p-6 text-center text-sm text-muted-foreground">Nothing matches this filter — try a different severity.</div>
      ) : null}
    </GlassSurface>
  );
}

function PagesTab({
  items,
  isLoading,
  error,
  onOpen,
}: {
  items: AuditPageSummary[];
  isLoading: boolean;
  error: unknown;
  onOpen: (id: string) => void;
}): JSX.Element {
  if (error) return <ErrorBanner error={error} />;
  if (isLoading)
    return (
      <div className="flex justify-center p-10">
        <Spinner />
      </div>
    );
  return (
    <GlassSurface className="p-4">
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase text-muted-foreground">
          <tr>
            <th className="py-2">Page</th>
            <th>Status</th>
            <th>Crit</th>
            <th>High</th>
            <th>Med</th>
            <th>Low</th>
            <th>Content</th>
            <th>Speed</th>
          </tr>
        </thead>
        <tbody>
          {items.map((p) => (
            <tr
              key={p.id}
              className="cursor-pointer border-t border-border/40 hover:bg-muted/30"
              onClick={() => onOpen(p.id)}
            >
              <td className="max-w-md truncate py-2 pr-2">
                <div className="font-medium">{p.title ?? p.url}</div>
                <div className="text-xs text-muted-foreground">{p.url}</div>
              </td>
              <td className="text-xs">{p.status_code}</td>
              <td className="text-xs">{p.critical_count}</td>
              <td className="text-xs">{p.high_count}</td>
              <td className="text-xs">{p.medium_count}</td>
              <td className="text-xs">{p.low_count}</td>
              <td className="text-xs">{p.content_quality_score ?? '—'}</td>
              <td className="text-xs">{p.performance_score ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </GlassSurface>
  );
}

function IssueDrawer({
  auditId,
  issueId,
  onClose,
}: {
  auditId: string;
  issueId: string;
  onClose: () => void;
}): JSX.Element {
  const { data, isLoading, error } = useAuditIssue(auditId, issueId);
  return (
    <Drawer onClose={onClose} title="Issue">
      {error ? (
        <ErrorBanner error={error} />
      ) : isLoading || !data ? (
        <Spinner />
      ) : (
        <div className="space-y-4">
          <div>
            <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
              <SeverityBadge severity={data.severity} />
              <span className="rounded-full bg-muted px-2 py-0.5">{data.bucket}</span>
              <code>{data.check_id}</code>
            </div>
            <h2 className="text-xl font-semibold">{data.title}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{data.summary}</p>
          </div>
          {data.llm_explanation ? (
            <section>
              <h3 className="text-sm font-semibold uppercase text-muted-foreground">
                What this means <span className="text-sky-500">· AI</span>
              </h3>
              <p className="mt-1 text-sm">{data.llm_explanation}</p>
            </section>
          ) : (
            <section className="rounded-md border border-sky-500/20 bg-sky-500/5 p-3 text-xs text-sky-100/80">
              <div className="mb-1 font-semibold text-sky-200">
                AI explanation not ready yet
              </div>
              We found this issue during the audit, but the plain-English
              explanation and recommended fix haven&apos;t been generated yet —
              usually because our AI briefly hit a limit. Click <em>Retry AI
              explanations</em> at the top of the audit page and we&apos;ll fill it in.
            </section>
          )}
          {data.llm_fix ? (
            <section>
              <h3 className="text-sm font-semibold uppercase text-muted-foreground">
                Recommended fix <span className="text-sky-500">· AI</span>
              </h3>
              <p className="mt-1 text-sm">{data.llm_fix}</p>
            </section>
          ) : null}
          {data.llm_fix_code ? (
            <section>
              <div className="mb-1 flex items-center justify-between">
                <h3 className="text-sm font-semibold uppercase text-muted-foreground">
                  Ready-to-paste code
                </h3>
                <button
                  type="button"
                  className="text-xs text-primary hover:underline"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(data.llm_fix_code ?? '');
                    } catch {}
                  }}
                >
                  Copy
                </button>
              </div>
              <pre className="max-h-64 overflow-auto rounded border border-border/40 bg-muted/40 p-3 text-xs">
                <code>{data.llm_fix_code}</code>
              </pre>
            </section>
          ) : null}
          {data.affected_urls_sample && data.affected_urls_sample.length > 0 ? (
            <section>
              <h3 className="text-sm font-semibold uppercase text-muted-foreground">
                Affected URLs ({data.affected_url_count})
              </h3>
              <ul className="mt-1 space-y-1">
                {data.affected_urls_sample.map((u, i) => (
                  <li key={`${u.url}-${i}`} className="break-all text-xs">
                    {u.url}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      )}
    </Drawer>
  );
}

function PageDrawer({
  auditId,
  pageId,
  onClose,
}: {
  auditId: string;
  pageId: string;
  onClose: () => void;
}): JSX.Element {
  const { data, isLoading, error } = useAuditPageDetail(auditId, pageId);
  return (
    <Drawer onClose={onClose} title="Page">
      {error ? (
        <ErrorBanner error={error} />
      ) : isLoading || !data ? (
        <Spinner />
      ) : (
        <div className="space-y-4">
          <div>
            <div className="text-xs text-muted-foreground">{data.page.url}</div>
            <h2 className="text-xl font-semibold">{data.page.title ?? '(no title)'}</h2>
            <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
              <span>Status {data.page.statusCode}</span>
              <span>Clicks from home {data.page.depth ?? '—'}</span>
              <span>Words {data.page.wordCount ?? '—'}</span>
              <span>Found via {data.page.discoveredFrom ?? '—'}</span>
            </div>
          </div>
          {data.score ? (
            <div className="grid grid-cols-4 gap-3 rounded border border-border/40 p-3">
              <Stat label="Critical" value={data.score.criticalCount} />
              <Stat label="High" value={data.score.highCount} />
              <Stat label="Medium" value={data.score.mediumCount} />
              <Stat label="Low" value={data.score.lowCount} />
              <Stat label="Content quality" value={data.score.contentQualityScore ?? '—'} />
              <Stat label="Page speed" value={data.score.performanceScore ?? '—'} />
            </div>
          ) : null}
          <section>
            <h3 className="mb-2 text-sm font-semibold uppercase text-muted-foreground">
              Issues on this page
            </h3>
            {data.issues.length === 0 ? (
              <div className="text-sm text-muted-foreground">This page is clean — no issues found.</div>
            ) : (
              <ul className="space-y-2">
                {data.issues.map((i) => (
                  <li
                    key={i.id}
                    className="rounded border border-border/40 p-3"
                  >
                    <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
                      <SeverityBadge severity={i.severity as Severity} />
                      <span className="rounded-full bg-muted px-2 py-0.5">{i.bucket}</span>
                    </div>
                    <div className="text-sm font-medium">{i.title}</div>
                    <div className="text-xs text-muted-foreground">{i.summary}</div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}
    </Drawer>
  );
}

function Drawer({
  children,
  onClose,
  title: _t,
}: {
  children: React.ReactNode;
  onClose: () => void;
  title: string;
}): JSX.Element {
  return (
    <div
      className="fixed inset-0 z-40 bg-black/60"
      role="dialog"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="fixed inset-y-0 right-0 z-50 w-full max-w-2xl overflow-y-auto border-l border-border/60 bg-background p-6 shadow-xl">
        <div className="mb-4 flex justify-end">
          <button
            onClick={onClose}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            Close
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | string }): JSX.Element {
  return (
    <div>
      <div className="text-xs uppercase text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}

function SeverityBadge({ severity }: { severity: Severity }): JSX.Element {
  const colors: Record<Severity, string> = {
    critical: 'bg-red-500/10 text-red-500',
    high: 'bg-orange-500/10 text-orange-500',
    medium: 'bg-yellow-500/10 text-yellow-500',
    low: 'bg-neutral-500/10 text-neutral-500',
  };
  return (
    <span className={cn('rounded px-2 py-0.5 text-xs font-semibold uppercase', colors[severity])}>
      {severity}
    </span>
  );
}

function MetaSidebar({
  audit,
  issueCount,
}: {
  audit: Audit;
  issueCount: number;
}): JSX.Element {
  const duration = useMemo(() => {
    if (!audit.started_at) return null;
    const end = audit.completed_at ?? audit.failed_at;
    const start = new Date(audit.started_at).getTime();
    const endTs = end ? new Date(end).getTime() : Date.now();
    const ms = Math.max(0, endTs - start);
    return formatDuration(ms);
  }, [audit.started_at, audit.completed_at, audit.failed_at]);

  const items: Array<{ icon: typeof Activity; label: string; value: string }> = [
    { icon: Activity, label: 'Issues found', value: String(issueCount) },
    { icon: Clock, label: 'How long it took', value: duration ?? '—' },
    {
      icon: Calendar,
      label: 'Started',
      value: audit.started_at ? new Date(audit.started_at).toLocaleString() : '—',
    },
    {
      icon: CheckCircle2,
      label: 'Completed',
      value: audit.completed_at
        ? new Date(audit.completed_at).toLocaleString()
        : audit.failed_at
          ? `failed ${new Date(audit.failed_at).toLocaleString()}`
          : '—',
    },
  ];

  return (
    <GlassSurface className="p-4">
      <div className="mb-3 text-[10px] uppercase tracking-wider text-muted-foreground">
        Audit details
      </div>
      <dl className="space-y-3">
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <div key={item.label} className="flex items-start gap-3">
              <span className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-md bg-background/70 ring-1 ring-border/60">
                <Icon className="h-3.5 w-3.5 text-muted-foreground" />
              </span>
              <div className="min-w-0 flex-1">
                <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  {item.label}
                </dt>
                <dd className="truncate text-sm text-foreground">{item.value}</dd>
              </div>
            </div>
          );
        })}
      </dl>
      <div className="mt-4 rounded-md border border-border/60 bg-background/40 p-3">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">What we checked</div>
        <div className="mt-1 text-xs text-foreground/90">
          Up to {audit.scope.max_pages} pages · {audit.scope.max_depth} clicks deep ·{' '}
          {audit.scope.render_js === 'always'
            ? 'JavaScript rendered on every page'
            : audit.scope.render_js === 'auto'
              ? 'JavaScript rendered when needed'
              : 'HTML only'}
        </div>
      </div>
      {/* Notices moved to top-level so they render on every tab. */}
      {audit.failure_reason ? (
        <div className="mt-3 rounded-md border border-rose-500/30 bg-rose-500/5 p-3 text-xs text-rose-200">
          {audit.failure_reason}
        </div>
      ) : null}
    </GlassSurface>
  );
}

interface AuditNotice {
  code: string;
  severity?: 'info' | 'warning';
  message: string;
  details?: Record<string, unknown>;
}

interface ResumeNarrationState {
  status: 'starting' | 'running' | 'completed' | 'failed';
  phase?: 'narrating' | 'rendering' | 'reconciling';
  workflow_id?: string;
  started_at?: string;
  updated_at?: string;
  issues_narrated_on_resume?: number;
  issues_remaining?: number;
  rate_limited?: boolean;
  reason?: string;
}

/**
 * Notices panel — renders `audits.warnings` grouped by severity so
 * informational messages (audit completed, AI narration skipped) don't
 * look like problems. Adds a "Retry AI narration" button when the info
 * notices call for it — this hits `POST /v1/audits/:id/resume-narration`.
 *
 * Persistent state — resume workflow phase lives on `audits.progress.resume_narration`
 * (starting / running / completed / failed) so it survives refresh. The panel
 * polls the audit every 2s while in an active phase and stops the moment the
 * server-reported status becomes terminal. Failures show a clear reason.
 */
function AuditNotices({
  auditId,
  audit,
  notices,
  failedButScored = false,
}: {
  auditId: string;
  audit: Audit;
  notices: AuditNotice[];
  /** Failed after scoring — crawl/score kept; Retry AI recovers without re-crawl. */
  failedButScored?: boolean;
}): JSX.Element {
  const infos = notices.filter((n) => (n.severity ?? 'warning') === 'info');
  const warnings = notices.filter((n) => (n.severity ?? 'warning') !== 'info');
  const canRetryNarration =
    failedButScored ||
    infos.some((n) => n.code === 'llm.rate_limited' || n.code === 'llm.summary_failed');
  const resume = useResumeNarration(auditId);
  const { toast } = useToast();
  const qc = useQueryClient();

  const resumeState =
    ((audit.progress as { resume_narration?: ResumeNarrationState } | undefined)
      ?.resume_narration) ?? null;
  const isActive =
    resumeState?.status === 'starting' || resumeState?.status === 'running';
  const isFailed = resumeState?.status === 'failed';

  // Poll audit + issues every 2s while a resume workflow is in-flight so the
  // UI reflects the worker's progress writes without a page refresh.
  useEffect(() => {
    if (!isActive) return;
    const t = setInterval(() => {
      void qc.invalidateQueries({ queryKey: ['audit', auditId] });
      void qc.invalidateQueries({ queryKey: ['audit-issues', auditId] });
    }, 2000);
    return () => clearInterval(t);
  }, [isActive, qc, auditId]);

  // Fire a "completed" toast exactly once, when transitioning from active → terminal.
  const previousStatusRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const prev = previousStatusRef.current;
    const cur = resumeState?.status;
    previousStatusRef.current = cur;
    if (prev && (prev === 'starting' || prev === 'running')) {
      if (cur === 'completed') {
        toast({
          title: 'AI explanations added',
          description:
            typeof resumeState?.issues_narrated_on_resume === 'number'
              ? `Filled in ${resumeState.issues_narrated_on_resume} more issue${resumeState.issues_narrated_on_resume === 1 ? '' : 's'}.`
              : 'Your audit now has fresh AI-written explanations.',
        });
      } else if (cur === 'failed') {
        toast({
          title: "We couldn't finish the AI explanations",
          description: resumeState?.reason ?? "Something went wrong — check the notes at the top of the audit.",
        });
      }
    }
  }, [resumeState, toast]);

  return (
    <>
      {infos.length > 0 || resumeState ? (
        <div
          className={cn(
            'rounded-md border p-4 text-sm',
            isFailed
              ? 'border-rose-500/30 bg-rose-500/5 text-rose-100/90'
              : 'border-sky-500/30 bg-sky-500/5 text-sky-100/90',
          )}
        >
          <div className="mb-2 flex items-center justify-between gap-3">
            <div
              className={cn(
                'flex items-center gap-2 font-semibold',
                isFailed ? 'text-rose-200' : 'text-sky-200',
              )}
            >
              <FileWarning className="h-4 w-4" />
              {isActive
                ? phaseLabel(resumeState?.phase, resumeState?.status)
                : isFailed
                  ? "We couldn't finish the AI explanations"
                  : failedButScored
                    ? 'Score ready — AI narration still needed'
                    : 'Audit is complete — a few notes'}
            </div>
            {(canRetryNarration || isFailed) && !isActive ? (
              <Button
                size="sm"
                variant="outline"
                disabled={resume.isPending}
                onClick={async () => {
                  try {
                    await resume.mutateAsync();
                  } catch (e) {
                    toastError(toast, e);
                  }
                }}
              >
                <RotateCw
                  className={cn('h-4 w-4', resume.isPending && 'animate-spin')}
                />
                Retry AI explanations
              </Button>
            ) : isActive ? (
              <span
                className="inline-flex items-center gap-2 rounded-md border border-sky-500/40 bg-sky-500/10 px-3 py-1 text-xs text-sky-200"
                aria-live="polite"
              >
                <RotateCw className="h-3.5 w-3.5 animate-spin" />
                In progress
              </span>
            ) : null}
          </div>
          {isActive ? (
            <div className="text-xs text-sky-100/80">
              We&apos;re writing the missing AI explanations in the background. This page
              updates on its own — feel free to leave it open or come back later.
              {typeof resumeState?.issues_narrated_on_resume === 'number' &&
              typeof resumeState?.issues_remaining === 'number' ? (
                <div className="mt-1 font-mono text-[11px] text-sky-100/70">
                  filled in: {resumeState.issues_narrated_on_resume} ·
                  still to do: {resumeState.issues_remaining}
                </div>
              ) : null}
            </div>
          ) : isFailed ? (
            <div className="text-xs text-rose-100/80">
              We couldn&apos;t finish adding the AI explanations. Your audit&apos;s score,
              findings, and PDF from the last successful run are still valid — you can
              try again below. No re-crawl needed.
              {resumeState?.reason ? (
                <div className="mt-1 font-mono text-[11px] text-rose-200/80">
                  reason: {resumeState.reason}
                </div>
              ) : null}
            </div>
          ) : failedButScored && infos.length === 0 ? (
            <div className="text-xs text-sky-100/80">
              The site crawl, checks, and score already finished. Only AI narration failed.
              Click <span className="font-medium">Retry AI explanations</span> to generate
              the narrative — you do not need to start a new audit or re-crawl.
            </div>
          ) : (
            <ul className="space-y-1.5 text-xs">
              {infos.map((n, i) => (
                <li key={`${n.code}-${i}`}>{n.message}</li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
      {warnings.length > 0 ? (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-100/90">
          <div className="mb-1 flex items-center gap-2 font-semibold text-amber-200">
            <FileWarning className="h-4 w-4" />
            Warnings ({warnings.length})
          </div>
          <ul className="space-y-1">
            {warnings.slice(0, 5).map((w, i) => (
              <li key={`${w.code}-${i}`}>
                <span className="font-mono text-amber-400">{w.code}</span> {w.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </>
  );
}

function phaseLabel(phase?: string, status?: string): string {
  if (status === 'starting') return 'Getting started on the AI explanations…';
  if (phase === 'narrating') return 'Writing plain-English explanations…';
  if (phase === 'rendering') return 'Rebuilding the PDF report…';
  if (phase === 'reconciling') return 'Saving the updates…';
  return 'Adding AI explanations…';
}

function formatDuration(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}
